// Sidebar — 主导航
// 桌面 (md+): 常驻左侧 240px 宽
// 移动 (<md): 通过 hamburger 触发 drawer 滑入
import { Link, useLocation } from '@tanstack/react-router';
import type { CompanyInfo } from '../lib/api';
import { Icon } from './Icon';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  match?: 'exact' | 'prefix';
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'circle', match: 'exact' },
  { to: '/customers', label: '客户', icon: 'circle-dot' },
  { to: '/pipeline', label: 'Pipeline', icon: 'bar-chart-3' },
  { to: '/inbox', label: '收件箱', icon: 'mail' },
  { to: '/inquiries', label: '询盘', icon: 'mail' },
  { to: '/quotes', label: '报价', icon: 'diamond' },
  { to: '/orders', label: '订单', icon: 'list' },
  { to: '/finance', label: '财务', icon: 'circle-dollar-sign' },
  { to: '/sourcing', label: '找客', icon: 'crosshair' },
  { to: '/compliance', label: '合规', icon: 'check' },
  { to: '/coach', label: '教练', icon: 'sparkles' },
  { to: '/outbound', label: '外发', icon: 'arrow-up-right' },
  { to: '/ocr', label: 'OCR', icon: 'sparkles' },
  { to: '/onboarding', label: '新手引导', icon: 'book-open' },
  { to: '/auto-prospect', label: '自动拓客', icon: 'bot' },
  { to: '/settings', label: '设置', icon: 'settings' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  currentCompany: CompanyInfo | null;
}

function isActive(pathname: string, item: NavItem): boolean {
  if (item.match === 'exact') return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

export function Sidebar({ open, onClose, currentCompany }: SidebarProps) {
  const location = useLocation();

  return (
    <>
      {/* 移动端 backdrop */}
      <div
        className={`md:hidden fixed inset-0 bg-ink/40 z-40 transition-opacity duration-200 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="关闭菜单"
      />

      <aside
        className={`
          fixed md:sticky top-0 left-0 h-screen w-60 shrink-0
          bg-surface border-r border-line z-50
          flex flex-col
          transition-transform duration-200 ease-out
          ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        aria-label="主导航"
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-line flex items-center gap-3">
          <div className="w-8 h-8 rounded-md bg-accent text-white flex items-center justify-center text-sm font-bold tracking-tight">
            贸
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-ink leading-tight">Trade OS</div>
            <div className="text-[10px] text-ink-3 leading-tight tracking-wider uppercase">
              Triangle Architecture
            </div>
          </div>
          {/* 移动端 close */}
          <button
            type="button"
            onClick={onClose}
            className="md:hidden w-7 h-7 flex items-center justify-center text-ink-2 hover:bg-surface-2 rounded"
            aria-label="关闭菜单"
          >
            <Icon name="x" size="sm" />
          </button>
        </div>

        {/* 当前公司 */}
        {currentCompany && (
          <div className="px-3 py-3 border-b border-line">
            <div className="text-[10px] uppercase tracking-wider text-ink-3 mb-1.5 px-2">
              当前公司
            </div>
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-surface-2">
              <span className="text-base">{currentCompany.flag}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{currentCompany.name}</div>
                <div className="text-[10px] text-ink-3">{currentCompany.code}</div>
              </div>
            </div>
          </div>
        )}

        {/* 导航 */}
        <nav className="flex-1 overflow-y-auto py-3">
          <ul className="px-2 space-y-0.5">
            {NAV.map((item) => {
              const active = isActive(location.pathname, item);
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={onClose}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-md text-sm
                      transition-colors duration-150
                      ${
                        active
                          ? 'bg-accent-soft text-accent-2 font-medium'
                          : 'text-ink-2 hover:bg-surface-2 hover:text-ink'
                      }
                    `}
                  >
                    <Icon
                      name={item.icon}
                      size="md"
                      className={`w-5 h-5 ${active ? 'text-accent' : 'text-ink-3'}`}
                    />
                    <span>{item.label}</span>
                    {active && <span className="ml-auto w-1 h-1 rounded-full bg-accent" />}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* 底部 footer */}
        <div className="px-5 py-3 border-t border-line text-[10px] text-ink-3">
          v1.33 · Wabi-sabi
        </div>
      </aside>
    </>
  );
}
